import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { ApiService, Device, IDeviceData, TWAMP_MODE } from '@nrwl/common-lib';
import { first, takeUntil } from 'rxjs/operators';
import { isArray, isString } from 'lodash';

export const ReflectorTypes = {
  TWMP_REFLECTOR: 'TWAMP Reflector',
  TWMP_LIGHT_REFLECTOR: 'TWAMP Light Reflector',
  PING: 'Ping Reflector'
};

@Injectable({
  providedIn: 'root'
})
export class UnmanagedDevicesService {
  unmanagedPaginationURL = '/vibe/inventory/v5/queryUnmanagedDevices/scroll?size=10';
  baseUrl = '/vibe/inventory/v4/';
  realtimemonitorcache = [];
  cache = new BehaviorSubject([]);
  unmanagedDevices = [];
  //eslint-disable-next-line
  updated: Subject<Array<any>>;
  queryLoading = false;
  pageSize: number;
  initialQuery = false;
  deviceQueryComplete = false;
  overallDevices = [];
  totalCount = 0;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(private readonly apiService: ApiService) {
    this.pageSize = 10000;
  }

  getEntityName(): string {
    return 'unmanaged-devices';
  }

  //eslint-disable-next-line
  addUnmanagedDevices(data): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.post$<any>(`${this.baseUrl}addUnManagedDevices`, data);
  }
  //eslint-disable-next-line
  queryUnmanagedDevices(data): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.post$<any>(`${this.baseUrl}queryUnmanagedDevices`, data);
  }
  //eslint-disable-next-line
  updateUnmanagedDevice(data): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.put$<any>(`${this.baseUrl}updateUnmanagedDevices`, data);
  }
  //eslint-disable-next-line
  deleteDevices(data): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.delete$<any>(`${this.baseUrl}removeUnmanagedDevices?force=true`, data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValues(): any[] {
    return this.getUnmanagedDevices();
  }

  getValuesAsync(): Observable<Array<[]>> {
    const devices = this.getValues();
    if (!devices || devices.length === 0) {
      return this.updateUnmanagedDevices(this.realtimemonitorcache).pipe(first());
    }
    return of(devices).pipe(first());
  }

  getUpdatedDevices(): Observable<Array<[]>> {
    return this.updateUnmanagedDevices(this.realtimemonitorcache).pipe(first());
  }

  getUnmanagedDevices(): Array<Device> {
    return this.chooseCacheModel();
  }

  chooseCacheModel(): Array<Device> {
    // simplified implementation/no support for history mode
    return this.realtimemonitorcache;
  }

  // eslint-disable-next-line
  findDeviceByID(deviceId: string): Device {
    for (const device of this.getUnmanagedDevices()) {
      if (device.getId() === deviceId) {
        return device;
      }
    }
  }

  // eslint-disable-next-line
  findDeviceByName(deviceName: string): Device {
    for (const device of this.getUnmanagedDevices()) {
      if (device.getName() === deviceName) {
        return device;
      }
    }
  }

  //eslint-disable-next-line
  updateUnmanagedDevices(cache: any, scrollId?: string): any {
    this.initialQuery = false;
    if (this.queryLoading) {
      return this.updated;
    }
    this.updated = new Subject();
    this.queryLoading = true;
    this.unmanagedPaginationURL = '/vibe/inventory/v5/queryUnmanagedDevices/scroll?';
    if (scrollId) {
      this.unmanagedPaginationURL =
        this.unmanagedPaginationURL + 'queryId=' + scrollId + '&size=' + this.pageSize;
    } else {
      this.unmanagedPaginationURL = this.unmanagedPaginationURL + 'size=' + this.pageSize;
    }
    this.apiService
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      .post$<any>(this.unmanagedPaginationURL, {})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.queryLoading = false;
          if (response && response?.totalRecords === 0) {
            this.totalCount = response.totalRecords;
            this.overallDevices = [];
            this.assignUnmanagedDevices(cache, []);
            this.updated.next(this.getUnmanagedDevices());
            this.paginateDevices(0, response.queryId);
          }
          if (response && response.OutputDeviceList?.length >= 0) {
            if (!scrollId) {
              // First api call clear the monitors array
              this.overallDevices = [];
            }
            if (cache.length === 0) {
              this.initialQuery = true;
            }
            this.unmanagedDevices = response.OutputDeviceList;
            this.totalCount = response.totalRecords;
            const responseData = this.transformResponse(response.OutputDeviceList);
            this.overallDevices.push(...responseData);
            this.assignUnmanagedDevices(cache, responseData);
            this.updated.next(this.getUnmanagedDevices());
            this.paginateDevices(response.OutputDeviceList.length, response.queryId);
            // return this.chooseCacheModel();
          }
        },
        () => {
          this.queryLoading = false;
        },
        () => {
          this.queryLoading = false;
          this.updated.complete();
        }
      );
    return this.updated;
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  //eslint-disable-next-line
  assignUnmanagedDevices(cache: any, data: Array<any>): void {
    // simplified algo, that simply replaces full cache with new list
    // notice we preserve the array object!
    cache.length = 0;
    for (const entry of this.overallDevices) {
      cache.push(entry);
    }

    // this.cache.next(this.chooseCacheModel());
  }
  //eslint-disable-next-line
  getPort(data: any): string {
    const twLightPort =
      data?.capabilities && data?.capabilities['twamp-light-reflector']
        ? data?.capabilities['twamp-light-reflector']['udp-port']
        : '';
    const twPort =
      data?.capabilities && data?.capabilities['twamp-reflector']
        ? data?.capabilities['twamp-reflector']['control-server-port']
        : '';
    return twPort ? twPort : twLightPort ? twLightPort : '';
  }
  //eslint-disable-next-line
  transformResponse(dataIn: any): Array<Device> {
    dataIn = isString(dataIn) ? JSON.parse(dataIn) : dataIn;
    const response = [];
    if (dataIn !== null && isArray(dataIn)) {
      dataIn.forEach(result => {
        const configIn = {} as IDeviceData;
        configIn.id = result.id;
        configIn.uniqueId = result.id;
        configIn.name = result.alias || result.name;
        configIn.nameId = result.alias || result.name;
        configIn.deviceName = result.alias;
        configIn.type = 'unmanaged';
        configIn.site = Number(result.extension?.location?.address) || 0;
        configIn.location = result.extension?.location?.address || '';
        configIn.lat = result.extension?.location?.position?.lat || 0;
        configIn.lon = result.extension?.location?.position?.lon || 0;
        configIn.status = result.status;
        configIn.substatus = result.substatus;
        configIn.model = result.model;
        configIn.lastUpgradeMessage = result.lastUpgradeMessage;
        configIn.lastUpdateStartTime = result.lastUpdateStartTime;
        configIn.lastUpgradeMessageTime = result.lastUpgradeMessageTime;
        configIn.capabilities = result.extension?.capabilities;
        configIn.connections = result.connections ? result.connections : null;
        configIn.ipaddress = this.getIPAddressAndType(result.extension).ip;
        this.parseParams(configIn, result);
        this.runningReflectors(configIn, result);

        const listObj: Device = new Device(configIn);

        response.push(listObj);
      });
    }
    return response;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  parseParams(configIn: IDeviceData, result: any): void {
    configIn.params = [];
    configIn.params.push({ name: 'platform', value: 'Unmanaged' });
    configIn.params.push({ name: 'model', value: result.model?.model });
    configIn.params.push({ name: 'version', value: result.model?.version });
    configIn.params.push({ name: 'mac', value: result.extension?.capabilities?.mac });
    configIn.params.push({ name: 'firstRegistrationTime', value: result.firstRegistrationTime });
    configIn.params.push({ name: 'lastHeartBeatTime', value: result.lastHeartBeatTime });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  runningReflectors(configIn: IDeviceData, result: any): void {
    configIn['running-reflectors'] = [];
    if (result?.extension?.capabilities) {
      if (result.extension.capabilities['twamp-reflector']) {
        configIn['running-reflectors'].push({
          name: result.alias,
          ip: result.extension.capabilities['twamp-reflector']['control-server-ip'],
          port: result.extension.capabilities['twamp-reflector']['control-server-port'],
          type: 'twamp-reflector',
          twampMode: TWAMP_MODE.FULL,
          phyId: ''
        });
      }
      if (result.extension.capabilities['twamp-light-reflector']) {
        configIn['running-reflectors'].push({
          name: result.alias,
          ip: result.extension.capabilities['twamp-light-reflector'].ip,
          port: result.extension.capabilities['twamp-light-reflector']['udp-port'],
          type: 'twamp-light-reflector',
          twampMode: TWAMP_MODE.LIGHT,
          phyId: ''
        });
      }
      if (result.extension.capabilities['twamp-ping-reflector']) {
        configIn['running-reflectors'].push({
          name: result.alias,
          ip: result.extension.capabilities['twamp-ping-reflector'].ip,
          port: '',
          type: 'twamp-ping-reflector',
          twampMode: TWAMP_MODE.PING,
          payload: 'ping',
          phyId: ''
        });
      }
    }
  }

  //   eslint-disable-next-line
  getIPAddressAndType(data: any): any {
    const twLightIP =
      data?.capabilities && data?.capabilities['twamp-light-reflector']
        ? data?.capabilities['twamp-light-reflector'].ip
        : '';
    const twIP =
      data?.capabilities && data?.capabilities['twamp-reflector']
        ? data?.capabilities['twamp-reflector']['control-server-ip']
        : '';
    const twPing =
      data?.capabilities && data?.capabilities['twamp-ping-reflector']
        ? data?.capabilities['twamp-ping-reflector'].ip
        : '';
    return {
      ip: twIP ? twIP : twLightIP ? twLightIP : twPing ? twPing : '',
      type: twIP
        ? ReflectorTypes.TWMP_REFLECTOR
        : twLightIP
        ? ReflectorTypes.TWMP_LIGHT_REFLECTOR
        : twPing
        ? ReflectorTypes.PING
        : ''
    };
  }
  paginateDevices(count: number, scrollId: string): void {
    if (count === this.pageSize) {
      this.deviceQueryComplete = false;
      if (this.initialQuery) {
        //Update the cache immediately when first response came back
        this.cache.next(this.chooseCacheModel());
      }
      this.updateUnmanagedDevices(this.getUnmanagedDevices(), scrollId);
    } else {
      this.deviceQueryComplete = true;
      //update the cache once all scroll api completed
      this.cache.next(this.chooseCacheModel());
    }
  }
  //eslint-disable-next-line
  createTestName(type) {
    switch (type) {
      case 'twamp-light-reflector': {
        return 'twamp-light';
      }
      case 'twamp-reflector': {
        return 'twamp';
      }
      case 'twamp-ping-reflector': {
        return 'ping';
      }
    }
  }

  getReflectorTypeDevices(devices): any {
    return devices.filter(reflector => {
      return (
        reflector.deviceData.capabilities.hasOwnProperty('twamp-light-reflector') ||
        reflector.deviceData.capabilities.hasOwnProperty('twamp-reflector') ||
        reflector.deviceData.capabilities.hasOwnProperty('twamp-ping-reflector')
      );
    });
  }
}
